import React, { useEffect, useRef, useState } from "react";
import api, { apiError, API, LOGO_URL, inr } from "@/lib/api";
import { offerText } from "@/components/PriceTag";
import { useAuth } from "@/context/AuthContext";
import PageHeader from "@/components/PageHeader";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import html2canvas from "html2canvas";
import QRCode from "qrcode";
import { Download, Megaphone, Copy, IdCard } from "lucide-react";
import { PremiumCard } from "@/components/VisitingCard";

const fmt = (d) => {
  try { return new Date(d + "T00:00:00").toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }); } catch { return d; }
};


export default function Flyer() {
  const { user } = useAuth();
  const token = localStorage.getItem("token");
  const [packages, setPackages] = useState([]);
  const [media, setMedia] = useState([]);
  const [groups, setGroups] = useState([]);
  const [muallems, setMuallems] = useState([]);
  const [form, setForm] = useState({ name: "", phone: "", package: "", photoId: "", address: "" });
  const [downloading, setDownloading] = useState(false);
  const [refCode, setRefCode] = useState("");
  const [qrUrl, setQrUrl] = useState("");
  const [franchiseQrUrl, setFranchiseQrUrl] = useState("");
  const [jobsQrUrl, setJobsQrUrl] = useState("");
  const [mode, setMode] = useState("flyer");
  const flyerRef = useRef(null);
  const cardRef = useRef(null);
  const refLink = refCode ? `${window.location.origin}/portal?ref=${refCode}` : "";

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/my-referral");
        setRefCode(data.referral_code);
        const link = `${window.location.origin}/portal?ref=${data.referral_code}`;
        const url = await QRCode.toDataURL(link, { margin: 1, width: 240, color: { dark: "#05261D", light: "#f5ecc8" } });
        setQrUrl(url);
        const mlink = `${window.location.origin}/agent?ref=${data.referral_code}`;
        const murl = await QRCode.toDataURL(mlink, { margin: 1, width: 240, color: { dark: "#05261D", light: "#f5ecc8" } });
        setFranchiseQrUrl(murl);
        setJobsQrUrl(await QRCode.toDataURL(`${window.location.origin}/jobs?ref=${data.referral_code}`, { margin: 1, width: 240, color: { dark: "#05261D", light: "#f5ecc8" } }));
      } catch { /* referral code optional */ }
    })();
  }, []);

  useEffect(() => {
    if (user && !form.name) setForm((f) => ({ ...f, name: user.name || "", phone: user.phone || "" }));
  }, [user]); // eslint-disable-line

  useEffect(() => {
    (async () => {
      try {
        const [p, m, g, mu, off] = await Promise.all([api.get("/pricing"), api.get("/media"), api.get("/groups"), api.get("/muallems").catch(() => ({ data: [] })), api.get("/offices").catch(() => ({ data: [] }))]);
        setPackages(p.data.packages || []);
        setMedia(m.data);
        setGroups(g.data);
        setMuallems(mu.data);
        const firstOff = (off.data || [])[0];
        if (firstOff) setForm((f) => ({ ...f, address: f.address || [firstOff.location_name, firstOff.address].filter(Boolean).join(", ") }));
        if (p.data.packages?.length) setForm((f) => ({ ...f, package: f.package || p.data.packages[0].name }));
      } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    })();
  }, []);

  const pkg = packages.find((p) => p.name === form.package);
  const pkgGroups = groups.filter((g) => g.package === form.package).slice(0, 3);
  const flyerMuallem = pkgGroups.map((g) => muallems.find((m) => m.id === g.muallem_id)).find(Boolean);
  const photos = media.filter((m) => m.kind !== "video");
  const photo = photos.find((m) => m.id === form.photoId) || photos[0];
  const photoUrl = photo ? `${API}/media/${photo.id}/file?auth=${token}` : null;

  const download = async () => {
    const target = mode === "card" ? cardRef.current : flyerRef.current;
    if (!target) return;
    setDownloading(true);
    try {
      const canvas = await html2canvas(target, { scale: 3, useCORS: true, backgroundColor: mode === "card" ? "#03050c" : "#05261D", imageTimeout: 15000, logging: false });
      const a = document.createElement("a");
      a.download = `bengal-umrah-${mode === "card" ? "visiting-card" : "flyer"}-${(form.name || "ad").replace(/\s+/g, "-").toLowerCase()}.png`;
      a.href = canvas.toDataURL("image/png");
      a.click();
      toast.success(mode === "card" ? "Visiting card downloaded — share it anywhere!" : "Flyer downloaded — share it on WhatsApp, Facebook & Instagram!");
    } catch (err) {
      toast.error("Could not generate image");
    } finally { setDownloading(false); }
  };

  return (
    <div data-testid="flyer-page">
      <PageHeader eyebrow="Marketing" title="Premium Flyer Maker"
        subtitle="Create premium flyers & visiting cards with your name, number and referral QR — share anywhere" />
      <div className="p-4 sm:p-8 grid lg:grid-cols-[340px_1fr] gap-6 items-start">
        <Card className="p-4 bg-card/40 border-border/60 space-y-3" data-testid="flyer-form">
          <div className="grid grid-cols-2 gap-2">
            <button type="button" data-testid="mode-flyer-btn" onClick={() => setMode("flyer")}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${mode === "flyer" ? "border-gold bg-gold/15 text-gold" : "border-border/60 text-muted-foreground hover:border-gold/40"}`}>
              <Megaphone className="w-3.5 h-3.5" /> Flyer
            </button>
            <button type="button" data-testid="mode-card-btn" onClick={() => setMode("card")}
              className={`flex items-center justify-center gap-1.5 py-2 rounded-lg border text-xs font-semibold transition-colors ${mode === "card" ? "border-gold bg-gold/15 text-gold" : "border-border/60 text-muted-foreground hover:border-gold/40"}`}>
              <IdCard className="w-3.5 h-3.5" /> Visiting Card
            </button>
          </div>
          <div><Label className="text-xs text-muted-foreground">Your name</Label><Input data-testid="flyer-name-input" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 bg-card/60" placeholder="e.g. Ayesha Rahman" /></div>
          <div><Label className="text-xs text-muted-foreground">Your contact number</Label><Input data-testid="flyer-phone-input" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1 bg-card/60" placeholder="+9198XXXXXXXX" /></div>
          {mode === "card" && (
            <div><Label className="text-xs text-muted-foreground">Address (on the card)</Label><Input data-testid="card-address-input" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} className="mt-1 bg-card/60" placeholder="Shop no, street, city" /></div>
          )}
          {mode === "flyer" && (
          <>
          <div>
            <Label className="text-xs text-muted-foreground">Package</Label>
            <Select value={form.package} onValueChange={(v) => setForm({ ...form, package: v })}>
              <SelectTrigger data-testid="flyer-package-select" className="mt-1 bg-card/60 border-border/60"><SelectValue placeholder="Choose package" /></SelectTrigger>
              <SelectContent className="bg-[#05261D] border-gold/20">
                {packages.map((p) => <SelectItem key={p.id} value={p.name}>{p.name} — {inr(p.price)}{p.mrp > p.price ? ` (was ${inr(p.mrp)})` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-muted-foreground">Gallery photo</Label>
            <div className="grid grid-cols-4 gap-2 mt-1.5">
              {photos.map((m) => (
                <button key={m.id} type="button" data-testid={`flyer-photo-${m.id}`} onClick={() => setForm({ ...form, photoId: m.id })}
                  className={`rounded-md overflow-hidden border-2 transition-colors ${form.photoId === m.id ? "border-gold" : "border-transparent hover:border-gold/40"}`}>
                  <img src={`${API}/media/${m.id}/file?auth=${token}`} alt={m.caption || "gallery"} className="w-full h-14 object-cover" />
                </button>
              ))}
            </div>
          </div>
          </>
          )}
          <Button data-testid="flyer-download-btn" onClick={download} disabled={downloading} className="w-full bg-gold text-[#0a2e22] hover:bg-gold/90 font-semibold gap-2">
            <Download className="w-4 h-4" /> {downloading ? "Generating…" : mode === "card" ? "Download Visiting Card (PNG)" : "Download Flyer (PNG)"}
          </Button>
          <Button variant="ghost" data-testid="flyer-copy-link-btn" disabled={!refCode}
            onClick={() => { navigator.clipboard.writeText(refLink); toast.success("Your referral link copied"); }}
            className="w-full text-gold border border-gold/30 hover:bg-gold/10 text-xs gap-2">
            <Copy className="w-3.5 h-3.5" /> Copy My Referral Link {refCode ? `(${refCode})` : ""}
          </Button>
          <p className="text-[10px] text-muted-foreground text-center">High-resolution image with your QR code — anyone who scans it is credited to you.</p>
        </Card>

        <div className="flex justify-center overflow-x-auto">
          {mode === "card" ? (
          <PremiumCard ref={cardRef} name={form.name} phone={form.phone} address={form.address} refCode={refCode} user={user}
            qrs={{ portal: qrUrl, agent: franchiseQrUrl, jobs: jobsQrUrl }} />
          ) : (
          <div ref={flyerRef} data-testid="flyer-preview"
            style={{ width: 400, borderRadius: 24, overflow: "hidden", background: "linear-gradient(160deg,#0c3527 0%,#05261D 55%,#0c3527 100%)", border: "2px solid #d4af37", padding: 18 }}>
            <div style={{ border: "1px solid rgba(212,175,55,0.55)", borderRadius: 16, padding: "22px 20px", textAlign: "center", background: "radial-gradient(circle at 50% 0%, rgba(212,175,55,0.10), transparent 60%)" }}>
              <img src={LOGO_URL} crossOrigin="anonymous" alt="Bengal Umrah House" style={{ width: 84, height: 84, borderRadius: "50%", margin: "0 auto", border: "2px solid #d4af37", objectFit: "cover" }} />
              <p style={{ color: "#d4af37", letterSpacing: 4, fontSize: 10, marginTop: 10, fontFamily: "Georgia, serif" }}>BENGAL UMRAH HOUSE</p>
              <p style={{ color: "#ffffff", fontSize: 21, marginTop: 3, fontFamily: "Georgia, serif" }}>Premium Umrah Journey</p>
              {photoUrl && <img src={photoUrl} crossOrigin="anonymous" alt="Umrah" style={{ width: "100%", height: 150, objectFit: "cover", borderRadius: 12, marginTop: 14, border: "1px solid rgba(212,175,55,0.6)" }} />}
              {pkg && (
                <>
                  <p style={{ color: "#d4af37", fontSize: 24, marginTop: 14, fontFamily: "Georgia, serif" }}>{pkg.name}</p>
                  <p style={{ color: "#ffffff", fontSize: 19, marginTop: 2 }}>{inr(pkg.price)} {offerText(pkg.price, pkg.mrp, pkg.offer_until) && <><span style={{ fontSize: 13, color: "rgba(255,255,255,0.55)", textDecoration: "line-through" }}>{inr(pkg.mrp)}</span> <span style={{ fontSize: 11, color: "#fff", background: "#dc2626", borderRadius: 999, padding: "1px 8px" }}>{offerText(pkg.price, pkg.mrp, pkg.offer_until)}</span></>} <span style={{ fontSize: 11, color: "rgba(255,255,255,0.6)" }}>per person</span></p>
                  <p style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, marginTop: 4 }}>{pkg.desc}</p>
                </>
              )}
              {pkgGroups.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <p style={{ color: "#d4af37", fontSize: 9, letterSpacing: 2 }}>NEXT GROUP DATES</p>
                  <p style={{ color: "#ffffff", fontSize: 12, marginTop: 3 }}>{pkgGroups.map((g) => fmt(g.date)).join("  ·  ")}</p>
                </div>
              )}
              {flyerMuallem && (
                <div data-testid="flyer-muallem" style={{ marginTop: 10, display: "flex", alignItems: "center", justifyContent: "center", gap: 8 }}>
                  {flyerMuallem.photo_path ? (
                    <img src={`${API}/muallems/${flyerMuallem.id}/photo?auth=${token}`} crossOrigin="anonymous" alt={flyerMuallem.name}
                      style={{ width: 34, height: 34, borderRadius: "50%", objectFit: "cover", border: "1.5px solid #d4af37" }} />
                  ) : (
                    <span style={{ width: 34, height: 34, borderRadius: "50%", border: "1.5px solid #d4af37", background: "rgba(212,175,55,0.15)", color: "#d4af37", fontSize: 15, fontFamily: "Georgia, serif", display: "flex", alignItems: "center", justifyContent: "center" }}>{flyerMuallem.name[0]}</span>
                  )}
                  <div style={{ textAlign: "left" }}>
                    <p style={{ color: "#d4af37", fontSize: 8, letterSpacing: 2 }}>LED BY MUALLEM</p>
                    <p style={{ color: "#ffffff", fontSize: 12, fontFamily: "Georgia, serif" }}>{flyerMuallem.name}</p>
                  </div>
                </div>
              )}
              <div style={{ borderTop: "1px solid rgba(212,175,55,0.4)", margin: "14px 6px" }} />
              <p style={{ color: "#d4af37", fontSize: 9, letterSpacing: 2 }}>BOOK YOUR SEAT WITH</p>
              <p style={{ color: "#ffffff", fontSize: 17, marginTop: 4, fontFamily: "Georgia, serif" }}>{form.name || "Your Name"}</p>
              <p style={{ color: "#ffffff", fontSize: 15, marginTop: 2, fontFamily: "monospace" }}>{form.phone || "+91 XXXXX XXXXX"}</p>
              {qrUrl && (
                <div style={{ marginTop: 12 }}>
                  <img src={qrUrl} alt="Scan to book" style={{ width: 92, height: 92, margin: "0 auto", borderRadius: 10, border: "2px solid #d4af37", background: "#f5ecc8", padding: 4 }} />
                  <p style={{ color: "#d4af37", fontSize: 9, letterSpacing: 2, marginTop: 6 }}>SCAN QR TO BOOK · CODE {refCode}</p>
                  <p style={{ color: "rgba(255,255,255,0.6)", fontSize: 8, marginTop: 3, wordBreak: "break-all", fontFamily: "monospace" }}>{refLink}</p>
                </div>
              )}
              <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 9, marginTop: 10 }}>Call / WhatsApp anytime · Bengal Umrah House Travels</p>
            </div>
          </div>
          )}
        </div>
      </div>
    </div>
  );
}
